/**
 * People, and the verification queue — FR-B2 and FR-B4, staff side. Redrawn on
 * the list shell in Wave 15B (C14 §10.6 items 1–2, ADR-0033; T-6.14): one page,
 * two lists as tabs — **Waiting for you** and **Verified**.
 *
 * The queue is the interesting half. Someone registered on the firm's website
 * and told us something about an entity; **nothing they typed has become a
 * client record**, and this screen is where a member of the firm turns the
 * claim into one — by linking them to an entity that already exists, or by
 * creating one (`VerifyDialog`).
 *
 * The `match` on a queue row is the firm's own client whose PAN the prospect
 * claimed. It is a **suggestion**: staff still choose, because a matching PAN
 * says the number is the same and nothing about whether this person may act
 * for that entity.
 *
 * A person has no page of their own, so no row here opens a record: a queue
 * row's one act is *Verify* (gated by `person:can_update`, as before), and a
 * verified person's client links live on each client's page.
 */

import { Button, Tabs, TabPanel, formatDate } from '@fiveox/ui'
import { UserCheck } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'

import {
  type ListColumn,
  ListPage,
  ListTable,
  ListToolbar,
  useListTable,
} from '../../components/page'
import { PERSON, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { type PersonRecord, type QueueRow, claimSummary, persons } from '../../lib/api/persons'
import { VerifyDialog } from './VerifyDialog'

type Tab = 'queue' | 'verified'

function added(value: string | null | undefined): string {
  return value ? formatDate(value) : '—'
}

export function People() {
  const { can } = useEvaluation()
  const mayResolve = can(PERSON, 'can_update')

  const [queue, setQueue] = useState<QueueRow[]>([])
  const [rows, setRows] = useState<PersonRecord[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [resolving, setResolving] = useState<QueueRow | null>(null)
  const [tab, setTab] = useState<Tab | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [q, all] = await Promise.all([persons.queue(), persons.list({ status: 'active' })])
      setQueue(q.data)
      setRows(all.data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'People could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const queueColumns = useMemo<ListColumn<QueueRow>[]>(
    () => [
      {
        key: 'full_name',
        label: 'Name',
        width: 200,
        required: true,
        phone: true,
        sortField: 'full_name',
        render: (row) => (
          <span className="block min-w-0">
            <span className="text-foreground block font-medium">{row.full_name}</span>
            <span className="text-muted-foreground block truncate text-xs">
              {row.email ?? row.mobile ?? '—'}
            </span>
          </span>
        ),
      },
      {
        key: 'claim',
        label: 'They say',
        width: 240,
        render: (row) => (
          <span className="text-muted-foreground block max-w-xs text-xs whitespace-normal">
            {claimSummary(row.submitted_details) || '—'}
          </span>
        ),
      },
      {
        key: 'match',
        label: 'Matches',
        width: 200,
        render: (row) =>
          row.match ? (
            <Link
              to={`/clients/${row.match.client_id}`}
              onClick={(event) => event.stopPropagation()}
              className="text-primary font-medium hover:underline"
            >
              {row.match.legal_name}
            </Link>
          ) : (
            <span className="text-muted-foreground">No client yet</span>
          ),
      },
      {
        key: 'created_at',
        label: 'Registered',
        width: 120,
        sortField: 'created_at',
        render: (row) => <span className="text-muted-foreground">{added(row.created_at)}</span>,
      },
    ],
    [],
  )

  const verifiedColumns = useMemo<ListColumn<PersonRecord>[]>(
    () => [
      {
        key: 'full_name',
        label: 'Name',
        width: 200,
        required: true,
        phone: true,
        sortField: 'full_name',
        render: (row) => <span className="text-foreground font-medium">{row.full_name}</span>,
      },
      {
        key: 'email',
        label: 'Email',
        width: 220,
        phone: true,
        sortField: 'email',
        render: (row) => <span className="text-muted-foreground">{row.email ?? '—'}</span>,
      },
      {
        key: 'mobile',
        label: 'Mobile',
        width: 150,
        sortField: 'mobile',
        render: (row) => <span className="text-muted-foreground">{row.mobile ?? '—'}</span>,
      },
      {
        key: 'created_at',
        label: 'Added',
        width: 120,
        sortField: 'created_at',
        render: (row) => <span className="text-muted-foreground">{added(row.created_at)}</span>,
      },
    ],
    [],
  )

  const queueList = useListTable(queue, {
    columns: queueColumns,
    search: (row) => [row.full_name, row.email, row.mobile, row.match?.legal_name],
    filters: [
      {
        key: 'match',
        label: 'Match',
        allLabel: 'Matched or not',
        options: [
          { value: 'match', label: 'Matches a client' },
          { value: 'none', label: 'No match' },
        ],
        match: (row, value) => (value === 'match' ? row.match !== null : row.match === null),
      },
    ],
    initialSort: { field: 'created_at', dir: 'asc' },
  })
  const verifiedList = useListTable(rows, {
    columns: verifiedColumns,
    search: ['full_name', 'email', 'mobile'],
    initialSort: { field: 'full_name', dir: 'asc' },
  })

  // The queue first while anybody is in it — it is the part that waits on the
  // firm — and the verified list otherwise.
  const active: Tab = tab ?? (queue.length > 0 ? 'queue' : 'verified')

  return (
    <>
      <ListPage
        title="People"
        description="The people who manage your clients’ accounts. A person is not a client — they act for one."
        loading={loading}
        error={error}
        isEmpty={queue.length === 0 && rows.length === 0}
        empty={{
          title: 'Nobody yet',
          description:
            'People who register on your website appear here until you link them to a client. You can also link someone from a client’s page.',
          action: (
            <Link to="/clients" className="text-primary text-sm font-medium hover:underline">
              Go to your clients
            </Link>
          ),
        }}
      >
        <div className="min-w-0 space-y-4">
          <Tabs
            idBase="people"
            active={active}
            onChange={(id) => setTab(id as Tab)}
            tabs={[
              { id: 'queue', label: 'Waiting for you', badge: queue.length || undefined },
              { id: 'verified', label: 'Verified', badge: rows.length || undefined },
            ]}
          />
          <TabPanel idBase="people" id="queue" active={active}>
            <div className="space-y-4">
              {queue.length > 0 && (
                <ListToolbar list={queueList} placeholder="Search by name, email or client" />
              )}
              <ListTable
                list={queueList}
                rowKey={(row) => row.id}
                empty="Nobody is waiting. People who register on your website appear here until you link them to a client."
                trailing={
                  mayResolve
                    ? {
                        header: '',
                        width: 120,
                        cell: (row) => (
                          <Button size="sm" onClick={() => setResolving(row)}>
                            <UserCheck size={14} aria-hidden /> Verify
                          </Button>
                        ),
                      }
                    : undefined
                }
              />
            </div>
          </TabPanel>
          <TabPanel idBase="people" id="verified" active={active}>
            <div className="space-y-4">
              {rows.length > 0 && (
                <ListToolbar list={verifiedList} placeholder="Search by name, email or mobile" />
              )}
              <ListTable
                list={verifiedList}
                rowKey={(row) => row.id}
                empty="Nobody verified yet. Link someone from a client’s page, or verify somebody who registered."
              />
            </div>
          </TabPanel>
        </div>
      </ListPage>

      {resolving && (
        <VerifyDialog
          row={resolving}
          onClose={() => setResolving(null)}
          onDone={() => {
            setResolving(null)
            void load()
          }}
        />
      )}
    </>
  )
}
